import type { ReactElement } from 'react';
import React, { cloneElement } from 'react';

import type { IClassNameProps } from '@bem-react/core';

import { Slot } from './Slot';
import { cnSlot } from './Slot.cn';
import type { SlotContent } from './Slot.types';

export type SlotWrapperProps<TValue> = IClassNameProps & {
    children?: SlotContent<TValue>;
    /**
     * Значение для рендера слота
     */
    value?: TValue;
    /**
     * Предустановленные аддоны левее переданных в слот
     */
    leftAddons?: ReactElement;
    /**
     * Предустановленные аддоны правее переданных в слот
     */
    rightAddons?: ReactElement;
    /**
     * Добавляет разделитель после контента в левом слоте
     * и до контента в правом, если не передан слот сверху
     */
    hasDefaultDivider?: boolean;
    /**
     * Добавляет разделители между элементами,
     * если не передан слот сверху
     */
    hasDefaultInlineDivider?: boolean;
    /**
     * Опрелеляет положение разделителей
     */
    place: 'left' | 'right';
    /**
     * Выключенноое состояние (передается в слот)
     */
    isDisabled?: boolean;
    /**
     * Определяет размеры отступов по умолчанию
     * @default input
     */
    type?: 'input' | 'action' | 'icon';
};

const areValidAddons = (addons?: ReactElement) =>
    addons?.type === React.Fragment ? addons?.props.children?.some(Boolean) : Boolean(addons);

export const SlotWrapper = <TValue,>({
    className,
    children,
    leftAddons,
    rightAddons,
    hasDefaultDivider,
    hasDefaultInlineDivider,
    value,
    place,
    isDisabled,
    type = 'input',
}: SlotWrapperProps<TValue>) => {
    const rendered = React.useMemo(
        () => (typeof children === 'function' ? children({ value }) : children),
        [children, value],
    );

    const content = React.useMemo(() => {
        if (rendered) {
            return cloneElement(
                rendered,
                { ...rendered.props, isDisabled },
                leftAddons,
                rendered.props.children,
                rightAddons,
            );
        }

        if (areValidAddons(leftAddons) || areValidAddons(rightAddons)) {
            return (
                <Slot
                    hasDivider={hasDefaultDivider}
                    hasInlineDivider={hasDefaultInlineDivider}
                    isDisabled={isDisabled}
                >
                    {leftAddons}
                    {rightAddons}
                </Slot>
            );
        }

        return null;
    }, [rendered, leftAddons, rightAddons, hasDefaultDivider, hasDefaultInlineDivider, isDisabled]);

    return <div className={cnSlot('Wrapper', { place, type }, [className])}>{content}</div>;
};
